import { useState, useRef, useCallback } from "react";
import { analyserFromStream } from "@/lib/audio";

function pickMime() {
  const cands = ["audio/webm", "audio/mp4", "audio/ogg"];
  if (typeof MediaRecorder === "undefined") return "";
  for (const c of cands) if (MediaRecorder.isTypeSupported(c)) return c;
  return "";
}
function extFor(mime) {
  if (!mime) return "m4a";
  if (mime.includes("webm")) return "webm";
  if (mime.includes("mp4")) return "mp4";
  if (mime.includes("ogg")) return "ogg";
  return "m4a";
}

// MediaRecorder hook. stop() resolves { blob, filename } with the correct
// container/extension so speech-to-text works on iOS Safari (mp4) and Android/desktop (webm).
export function useRecorder() {
  const [recording, setRecording] = useState(false);
  const [analyser, setAnalyser] = useState(null);
  const [error, setError] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const resolveRef = useRef(null);
  const mimeRef = useRef("");

  const start = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      mimeRef.current = mime || "audio/webm";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        setAnalyser(null);
        const type = mr.mimeType || mimeRef.current || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        if (resolveRef.current) { const r = resolveRef.current; resolveRef.current = null; r({ blob, filename: `audio.${extFor(type)}` }); }
      };
      mediaRef.current = mr;
      mr.start();
      setAnalyser(analyserFromStream(stream));
      setRecording(true);
      return true;
    } catch (e) {
      setError("Microphone permission denied");
      return false;
    }
  }, []);

  const stop = useCallback(() => {
    return new Promise((resolve) => {
      if (!mediaRef.current || mediaRef.current.state === "inactive") { resolve(null); return; }
      resolveRef.current = resolve;
      mediaRef.current.stop();
      setRecording(false);
      setTimeout(() => { if (resolveRef.current) { const r = resolveRef.current; resolveRef.current = null; r(null); } }, 5000);
    });
  }, []);

  return { recording, analyser, error, start, stop };
}
